import { createSlice } from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { getAdminUsers } from "./actions";

export type AdminUser = {
  id: number;
  name: string;
  email: string;
  phone?: string;
  company_name: string;
  package_name: string;
  status: string;
  status_class: string;
  trial: boolean;
  trial_expiration_date: string | null;
  days_remaining: number;
  created_at: string;
  is_admin: boolean;
};

export type ById<Model> = {
  loading: boolean;
  data: Model;
  error: any;
};

export type AdminUsersState = {
  error: any;
  token: null | string;
  loading: boolean;
  users: AdminUser[];
  invitableUsers: AdminUser[];
  byId: ById<AdminUser> | {};
  paging: Paging;
  pagingInvitableUsers: Paging;
  selectedUserId: number | null;
  stats: {
    total: number | null;
    paid: number | null;
    trial: number | null;
    expired: number | null;
    no_company: number | null;
  };
};

const initialState: AdminUsersState = {
  loading: false,
  token: null,
  error: false,
  users: [],
  invitableUsers: [],
  byId: {},
  paging: {} as Paging,
  pagingInvitableUsers: {} as Paging,
  selectedUserId: null,
  stats: {
    total: null,
    paid: null,
    trial: null,
    expired: null,
    no_company: null,
  },
};

export const adminUsersSlice = createSlice({
  initialState,
  name: "adminUsers",
  reducers: {},
  extraReducers: (builder) => {
    // GET USERS
    builder.addCase(getAdminUsers.fulfilled, (state, action) => {
      const payload = action.payload ?? {};
      const data = payload.data ?? payload;
      const users = Array.isArray(data?.users)
        ? data.users
        : Array.isArray(data)
        ? data
        : Array.isArray(payload?.users)
        ? payload.users
        : [];
      state.loading = false;
      state.error = false;
      state.users = users;
      const pagination =
        payload.pagination ??
        data?.pagination ??
        payload.meta ??
        data?.meta ??
        payload.paging ??
        data?.paging;
      if (pagination) {
        state.paging = {
          current_page: pagination.current_page ?? 1,
          from:
            pagination.from ??
            (pagination.current_page - 1) * pagination.per_page + 1,
          last_page: pagination.last_page ?? 1,
          per_page: pagination.per_page ?? 15,
          to: pagination.to ?? pagination.current_page * pagination.per_page,
          total: pagination.total ?? 0,
        };
      }
      if (payload.stats ?? data?.stats) {
        state.stats = payload.stats ?? data?.stats;
      }
    });
    builder.addCase(getAdminUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAdminUsers.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
  },
});

// Action creators are generated for each case reducer function
export default adminUsersSlice.reducer;
